import React, { useState, useContext } from 'react';
import AppContext from '../../contexts/AppContext';
import { MapPin, Search, Award } from 'lucide-react';
import { getDisplayLocation, haversineDistance } from '../../utils/location';
import Avatar from '../../components/common/Avatar';

const DiscoverView = () => {
  const { users, currentUser, viewUserProfile, toggleFollow } = useContext(AppContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [handicapFilter, setHandicapFilter] = useState('');
  const [distanceFilter, setDistanceFilter] = useState('');

  const nearbyUsers = users.filter(u => {
    if (u.id === currentUser.id || u.status !== 'active') return false;
    if ((currentUser.blockedUsers || []).includes(u.id)) return false;
    if ((u.blockedUsers || []).includes(currentUser.id)) return false;

    // Search term filter (strip leading @ so "@keith" matches handle "keith")
    const normalizedSearch = searchTerm.toLowerCase().replace(/^@/, '');
    const matchesSearch = normalizedSearch === '' ||
      u.name?.toLowerCase().includes(normalizedSearch) ||
      u.handle?.toLowerCase().includes(normalizedSearch);

    // Handicap filter with ranges
    let matchesHandicap = true;
    if (handicapFilter !== '') {
      if (handicapFilter === '0-10') {
        matchesHandicap = u.handicap >= 0 && u.handicap <= 10;
      } else if (handicapFilter === '10-20') {
        matchesHandicap = u.handicap >= 10 && u.handicap <= 20;
      } else if (handicapFilter === '15-25') {
        matchesHandicap = u.handicap >= 15 && u.handicap <= 25;
      } else if (handicapFilter === 'over-25') {
        matchesHandicap = u.handicap > 25;
      }
    }

    // Distance filter using real coordinates (Haversine formula)
    let matchesDistance = true;
    if (distanceFilter !== '') {
      const uLoc = u.location;
      const cLoc = currentUser.location;
      if (uLoc?.lat && uLoc?.lon && cLoc?.lat && cLoc?.lon) {
        const distance = haversineDistance(cLoc.lat, cLoc.lon, uLoc.lat, uLoc.lon);
        if (distanceFilter === '50+') {
          matchesDistance = distance > 50;
        } else {
          matchesDistance = distance <= parseInt(distanceFilter);
        }
      } else {
        // No coordinates available — match by zip code prefix (same area) or show all
        matchesDistance = distanceFilter === '50+' || parseInt(distanceFilter) >= 25;
      }
    }

    return matchesSearch && matchesHandicap && matchesDistance;
  }).map(u => {
    const uLoc = u.location;
    const cLoc = currentUser.location;
    let distance = null;
    if (uLoc?.lat && uLoc?.lon && cLoc?.lat && cLoc?.lon) {
      distance = haversineDistance(cLoc.lat, cLoc.lon, uLoc.lat, uLoc.lon);
    }
    return { ...u, _distance: distance };
  }).sort((a, b) => {
    if (a._distance === null && b._distance === null) return 0;
    if (a._distance === null) return 1;
    if (b._distance === null) return -1;
    return a._distance - b._distance;
  });

  return (
    <div className="max-w-2xl mx-auto p-4">
      {/* Search Box */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white w-5 h-5" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name or @handle..."
            className="w-full pl-10 pr-4 py-3 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
          />
        </div>
      </div>

      {/* Filter Box */}
      <div className="bg-black rounded-lg shadow border border-gray-700 p-4 mb-6">
        <h3 className="text-sm font-semibold text-white mb-3 flex items-center">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          Filters
        </h3>
        <div className="grid grid-cols-2 gap-4">
          {/* Handicap Filter */}
          <div>
            <label className="block text-xs font-medium text-white mb-2">Handicap</label>
            <select
              value={handicapFilter}
              onChange={(e) => setHandicapFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black text-sm"
            >
              <option value="">All Handicaps</option>
              <option value="over-25">Over 25</option>
              <option value="15-25">15-25</option>
              <option value="10-20">10-20</option>
              <option value="0-10">0-10</option>
            </select>
          </div>

          {/* Distance Filter */}
          <div>
            <label className="block text-xs font-medium text-white mb-2">Distance (miles)</label>
            <select
              value={distanceFilter}
              onChange={(e) => setDistanceFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black text-sm"
            >
              <option value="">Any Distance</option>
              <option value="5">Within 5 miles</option>
              <option value="10">Within 10 miles</option>
              <option value="15">Within 15 miles</option>
              <option value="20">Within 20 miles</option>
              <option value="25">Within 25 miles</option>
              <option value="30">Within 30 miles</option>
              <option value="35">Within 35 miles</option>
              <option value="40">Within 40 miles</option>
              <option value="45">Within 45 miles</option>
              <option value="50">Within 50 miles</option>
              <option value="50+">50+ miles</option>
            </select>
          </div>
        </div>

        {/* Clear Filters Button */}
        {(handicapFilter !== '' || distanceFilter !== '') && (
          <button
            onClick={() => {
              setHandicapFilter('');
              setDistanceFilter('');
            }}
            className="mt-3 text-xs text-[#16a34a] hover:text-[#15803d] font-medium"
          >
            Clear all filters
          </button>
        )}
      </div>

      <h2 className="text-lg font-bold text-white mb-4">Nearby Golfers ({nearbyUsers.length})</h2>
      
      <div className="space-y-3">
        {nearbyUsers.map(user => (
          <div key={user.id} className="bg-black rounded-lg shadow border border-gray-700 p-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Avatar user={user} size="lg" />
              <div>
                <button
                  onClick={() => viewUserProfile(user.id)}
                  className="font-bold text-white hover:text-[#16a34a] text-left"
                >
                  {user.name}
                </button>
                <p className="text-sm text-white">@{user.handle}</p>
                <p className="text-sm text-white">{user.skillLevel} • Handicap: {user.handicap}</p>
                <p className="text-xs text-white flex items-center">
                  <MapPin className="w-3 h-3 mr-1" />
                  {user._distance !== null ? `${user._distance.toFixed(1)} mi away` : getDisplayLocation(user.location)}
                </p>
                {user.isCoach && (
                  <span className="inline-block mt-1 px-2 py-1 bg-blue-800 text-pure-white text-xs rounded-full">
                    <Award className="w-3 h-3 inline mr-1" />
                    Coach
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={() => toggleFollow(user.id)}
              className={`px-2 py-1 text-pure-white text-xs rounded-lg ${
                currentUser.following?.includes(user.id)
                  ? 'bg-gray-600 hover:bg-gray-700'
                  : 'bg-[#16a34a] hover:bg-[#15803d]'
              }`}
            >
              {currentUser.following?.includes(user.id) ? 'Unfollow' : 'Follow'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== SEARCH ====================

export default DiscoverView;
